import type { ApolloCache } from '@apollo/client';

/**
 * Drop every cached `ListShiftsByWeek` result for an agency (GRAVY-10310).
 *
 * Dashboard V1's weekly grid runs `useListShiftsByWeekQuery` with no
 * `fetchPolicy`, and the client is built with no `defaultOptions` — so it reads
 * `cache-first`. `shiftsByWeek` is a field on the normalized `Agency` entity,
 * keyed by its arguments, so one cache entry exists per
 * `(startDate, filters, timeZone)` combination the admin has viewed.
 *
 * Orders are created over REST (React Query), so Apollo never learns the list
 * changed. Remounting the dashboard with the same variables is a cache hit that
 * issues no network request at all: the new order stays invisible until the
 * admin reloads the browser, hits the 120s poll, or happens to return via the
 * sidebar/navbar Dashboard link (the only handlers that set the Redux
 * `refetchDashboardData` flag).
 *
 * Evicting with no `args` removes every argument-keyed variant of the field, so
 * whichever week and filter combination the admin returns to is a miss and
 * refetches. Deliberately does NOT touch anything else in the cache.
 */
export function evictDashboardShiftWeeks(
  cache: ApolloCache<unknown>,
  agencyId: string | number | null | undefined,
): void {
  if (agencyId == null) return;

  const id = cache.identify({ __typename: 'Agency', id: String(agencyId) });
  if (!id) return;

  cache.evict({ id, fieldName: 'shiftsByWeek' });
  // Eviction leaves the Shift entities behind, unreachable from any query.
  cache.gc();
}
